/**
 * Login page — single auth surface for the web dashboard.
 *
 * Authenticated users are routed to their role surface on success:
 *   super_admin  → /admin
 *   brand_owner  → /owner
 *   customer/cashier → /mobile-only (web dashboard is management-only)
 */
import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore, routeForRole } from '@/lib/auth';

export default function LoginPage() {
  const navigate = useNavigate();
  const { signIn, user, loading, token } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Already logged in → route immediately
  useEffect(() => {
    if (loading) return;
    if (token && user) navigate(routeForRole(user.role), { replace: true });
  }, [loading, token, user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const u = await signIn(email, password);
      navigate(routeForRole(u.role), { replace: true });
    } catch (err) {
      setError(String(err?.detail ?? 'Login failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-4 relative">
      <div className="absolute inset-x-0 top-0 h-[420px] bg-gradient-to-b from-gold/15 to-transparent pointer-events-none" />
      <div className="relative w-full max-w-md">
        {/* Logo hero */}
        <div className="text-center mb-8">
          <img
            src="/onestamp-logo.png"
            alt="OneStamp"
            className="w-32 h-32 mx-auto rounded-2xl shadow-glow-gold-sm"
            data-testid="login-logo"
          />
          <p className="font-display mt-5 text-base italic text-gold tracking-wide" data-testid="login-tagline">
            One Card. One Tap. One Connection.
          </p>
          <h1 className="font-display mt-6 text-3xl font-bold tracking-tight">
            Welcome back
          </h1>
          <p className="text-white/55 mt-2 text-sm">
            Brand Owner &amp; Super Admin console
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-8 shadow-2xl"
          data-testid="login-form"
        >
          <label className="block">
            <span className="text-xs uppercase text-gold/85 tracking-widest font-semibold">
              Email
            </span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@brand.com"
              data-testid="login-email"
              className="mt-2 w-full bg-black/40 border border-white/10 rounded-2xl px-4 py-3 text-white focus:outline-none focus:border-gold/50 focus:shadow-glow-gold-sm transition"
            />
          </label>
          <label className="block mt-5">
            <span className="text-xs uppercase text-gold/85 tracking-widest font-semibold">
              Password
            </span>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              data-testid="login-password"
              className="mt-2 w-full bg-black/40 border border-white/10 rounded-2xl px-4 py-3 text-white focus:outline-none focus:border-gold/50 focus:shadow-glow-gold-sm transition"
            />
          </label>
          {error ? (
            <div className="mt-4 text-sm text-red-400" data-testid="login-error">
              {error}
            </div>
          ) : null}
          <button
            type="submit"
            disabled={submitting}
            data-testid="login-submit"
            className="mt-6 w-full bg-gold text-black font-semibold py-3 rounded-2xl hover:brightness-110 hover:shadow-glow-gold transition disabled:opacity-50"
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p className="text-center text-xs text-white/40 mt-8">
          Customers &amp; cashiers — please use the mobile app instead.
        </p>

        <div className="mt-6 text-center text-[11px] text-white/35">
          <Link
            to="/privacy"
            data-testid="staff-login-privacy-link"
            className="hover:text-white/70 transition-colors"
          >
            Privacy Policy
          </Link>
        </div>
      </div>
    </div>
  );
}
